import React, { useCallback, useState } from 'react';
import { buttonGroupClasses, ButtonGroup, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Theme } from '@mui/material/styles';

import { Button, ButtonProps } from '../Button';

export interface SplitButtonOption {
  label: React.ReactNode;
  value: string;
}

export interface SplitButtonProps
  extends Omit<ButtonProps, 'children' | 'onChange' | 'onClick'> {
  label: React.ReactNode;
  menuButtonAriaLabel: string;
  onClick: () => void;
  onSelectionChange: (value: string) => void;
  options: SplitButtonOption[];
  value: string;
}

const groupStyles = (theme: Theme) => ({
  borderRadius: 999,
  boxShadow: 'none',
  transition: theme.transitions.create('box-shadow', {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
  [`& .${buttonGroupClasses.firstButton}:hover`]: {
    borderRightColor: theme.palette.outline,
  },
});
const actionStyles = {
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
};
const toggleStyles = {
  minWidth: 40,
  px: 0.75,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
};
const menuListProps = { dense: true };

const SplitButton = React.memo(
  ({
    disabled,
    label,
    menuButtonAriaLabel,
    onClick,
    onSelectionChange,
    options,
    size = 'small',
    startIcon,
    sx,
    tone,
    value,
    variant,
    ...buttonProps
  }: SplitButtonProps) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const groupColor =
      tone === 'neutral' ? 'inherit' : tone === 'danger' ? 'error' : tone;

    const handleMenuOpen = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchor(event.currentTarget);
      },
      []
    );
    const handleMenuClose = useCallback(() => {
      setMenuAnchor(null);
    }, []);
    const handleSelection = useCallback(
      (event: React.MouseEvent<HTMLLIElement>) => {
        onSelectionChange(event.currentTarget.dataset.value || '');
        setMenuAnchor(null);
      },
      [onSelectionChange]
    );

    return (
      <>
        <ButtonGroup
          color={groupColor}
          size={size}
          sx={groupStyles}
          variant={variant}
        >
          <Button
            {...buttonProps}
            disabled={disabled}
            onClick={onClick}
            size={size}
            startIcon={startIcon}
            sx={[...(Array.isArray(sx) ? sx : [sx]), actionStyles]}
            tone={tone}
            variant={variant}
          >
            {label}
          </Button>
          <Button
            aria-expanded={Boolean(menuAnchor)}
            aria-haspopup="menu"
            aria-label={menuButtonAriaLabel}
            disabled={disabled}
            onClick={handleMenuOpen}
            size={size}
            sx={[...(Array.isArray(sx) ? sx : [sx]), toggleStyles]}
            tone={tone}
            type="button"
            variant={variant}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Menu
          anchorEl={menuAnchor}
          MenuListProps={menuListProps}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          {options.map((option) => (
            <MenuItem
              data-value={option.value}
              key={option.value}
              onClick={handleSelection}
              selected={option.value === value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }
);

SplitButton.displayName = 'SplitButton';

export { SplitButton };
